import { Tooltip } from '@brightdrop/bd-ui';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { translateText } from '@gm-commercial/translations';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, Checkbox, Popover, Typography } from '@mui/material';
import { useState } from 'react';
import ColumnsShowHideIcon from '../../../assets/icons/columns-03.svg?react';
import type {
  ColumnConfig,
  ShowHideColumnsComponentProps,
} from './ShowHideColumnsComponent.model';
import useStyles from './ShowHideColumnsComponent.styles';
import { useColumnSync } from './useColumnSync';

/** Restricts drag movement to the vertical axis (y only). */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});

interface ColumnItemContentProps {
  column: ColumnConfig;
  updateColumnVisibilityCallback: (column: string, visible: boolean) => void;
  classes: ReturnType<typeof useStyles>['classes'];
}

const ColumnItemContent = ({
  column,
  updateColumnVisibilityCallback,
  classes,
}: ColumnItemContentProps) => (
  <Typography
    variant="small_body_regular"
    sx={{ flex: 1, display: 'flex', alignItems: 'center' }}
  >
    <Checkbox
      className={classes.checkbox}
      checked={column.visible}
      data-testid={column.dataTestId}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        updateColumnVisibilityCallback(
          column.label,
          event.target.checked as boolean
        );
      }}
      disabled={column.alwaysVisible}
    />
    {column.label}
  </Typography>
);

interface SortableItemProps {
  id: string;
  column: ColumnConfig;
  updateColumnVisibilityCallback: (column: string, visible: boolean) => void;
  classes: ReturnType<typeof useStyles>['classes'];
}

const SortableItem = ({
  id,
  column,
  updateColumnVisibilityCallback,
  classes,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`${classes.checkboxWrapper} ${classes.checkboxWrapperDraggable}`}
    >
      <ColumnItemContent
        column={column}
        updateColumnVisibilityCallback={updateColumnVisibilityCallback}
        classes={classes}
      />
      <Box
        {...attributes}
        {...listeners}
        className={`drag-handle ${classes.dragHandle}`}
        data-testid={`drag-handle-${id}`}
      >
        <MenuIcon />
      </Box>
    </Box>
  );
};

const ShowHideColumnsComponent = ({
  columns,
  disabled,
  title,
  updateColumnVisibilityCallback,
  enableDragAndDrop = false,
  onReorderColumns,
}: ShowHideColumnsComponentProps) => {
  const tooltipText = translateText('asset:dialogTitles.editColumns');
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localColumns, setLocalColumns] = useColumnSync(
    columns,
    enableDragAndDrop
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalColumns((items) => {
        const oldIndex = items.findIndex(
          (item) => (item.columnId || item.label) === active.id
        );
        const newIndex = items.findIndex(
          (item) => (item.columnId || item.label) === over.id
        );

        // Guard against invalid indices or no-op moves
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          return items;
        }

        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        if (onReorderColumns) {
          onReorderColumns(reorderedItems);
        }

        return reorderedItems;
      });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box data-testid="show-hide-columns-container">
      {/* TODO: Replace with IconButton from bd-ui once accomodating design changes are complete */}
      <Tooltip
        content={tooltipText}
        showArrow
        placement="top"
        trigger={
          <Button
            onClick={handleClick}
            disabled={disabled}
            startIcon={<ColumnsShowHideIcon />}
            className={`${classes.showHideColumnsButton} ${anchorEl ? 'active' : ''}`}
            aria-label={tooltipText}
          />
        }
      />
      <Popover
        style={{ backgroundColor: 'transparent' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box className={classes.popoverContainer}>
          <Typography variant="overline_semibold" textTransform="uppercase">
            {title}
          </Typography>
          {enableDragAndDrop ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={localColumns.map((col) => col.columnId || col.label)}
                strategy={verticalListSortingStrategy}
              >
                {localColumns.map((column) => (
                  <SortableItem
                    key={column.columnId || column.label}
                    id={column.columnId || column.label}
                    column={column}
                    updateColumnVisibilityCallback={
                      updateColumnVisibilityCallback
                    }
                    classes={classes}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            localColumns.map((column) => (
              <Box key={column.label} className={classes.checkboxWrapper}>
                <ColumnItemContent
                  column={column}
                  updateColumnVisibilityCallback={
                    updateColumnVisibilityCallback
                  }
                  classes={classes}
                />
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ShowHideColumnsComponent;
